import { useCallback, useReducer } from 'react'
import { assessSite, areaScan, compareSites } from '../api/flux.js'

const initialState = {
  // Assessment
  assessment: null,
  assessLoading: false,
  assessError: null,

  // Area scan
  scanResult: null,
  scanLoading: false,
  scanError: null,

  // Comparison
  compareResult: null,
  compareLoading: false,
  compareError: null,

  // Compare site list (accumulated markers)
  compareQueue: [],   // [{lat, lon, label}]
}

function reducer(state, action) {
  switch (action.type) {
    case 'ASSESS_START':
      return { ...state, assessLoading: true, assessError: null }
    case 'ASSESS_SUCCESS':
      return { ...state, assessLoading: false, assessment: action.payload }
    case 'ASSESS_ERROR':
      return { ...state, assessLoading: false, assessError: action.payload }

    case 'SCAN_START':
      return { ...state, scanLoading: true, scanError: null, scanResult: null }
    case 'SCAN_SUCCESS':
      return { ...state, scanLoading: false, scanResult: action.payload }
    case 'SCAN_ERROR':
      return { ...state, scanLoading: false, scanError: action.payload }

    case 'COMPARE_START':
      return { ...state, compareLoading: true, compareError: null }
    case 'COMPARE_SUCCESS':
      return { ...state, compareLoading: false, compareResult: action.payload }
    case 'COMPARE_ERROR':
      return { ...state, compareLoading: false, compareError: action.payload }

    case 'COMPARE_QUEUE_ADD':
      if (state.compareQueue.length >= 10) return state
      return {
        ...state,
        compareQueue: [...state.compareQueue, action.payload],
      }
    case 'COMPARE_QUEUE_REMOVE':
      return {
        ...state,
        compareQueue: state.compareQueue.filter((_, i) => i !== action.index),
      }
    case 'COMPARE_QUEUE_CLEAR':
      return { ...state, compareQueue: [], compareResult: null }

    case 'CLEAR_ASSESSMENT':
      return { ...state, assessment: null, assessError: null }

    default:
      return state
  }
}

export function useAssessment() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const runAssessment = useCallback(async (params) => {
    dispatch({ type: 'ASSESS_START' })
    try {
      const result = await assessSite(params)
      dispatch({ type: 'ASSESS_SUCCESS', payload: result })
      return result
    } catch (err) {
      dispatch({ type: 'ASSESS_ERROR', payload: err.message })
      return null
    }
  }, [])

  const runAreaScan = useCallback(async (params) => {
    dispatch({ type: 'SCAN_START' })
    try {
      const result = await areaScan(params)
      dispatch({ type: 'SCAN_SUCCESS', payload: result })
    } catch (err) {
      dispatch({ type: 'SCAN_ERROR', payload: err.message })
    }
  }, [])

  const runComparison = useCallback(async (params) => {
    dispatch({ type: 'COMPARE_START' })
    try {
      const result = await compareSites(params)
      dispatch({ type: 'COMPARE_SUCCESS', payload: result })
    } catch (err) {
      dispatch({ type: 'COMPARE_ERROR', payload: err.message })
    }
  }, [])

  const addToCompareQueue = useCallback((site) => {
    dispatch({ type: 'COMPARE_QUEUE_ADD', payload: site })
  }, [])

  const removeFromCompareQueue = useCallback((index) => {
    dispatch({ type: 'COMPARE_QUEUE_REMOVE', index })
  }, [])

  const clearCompareQueue = useCallback(() => {
    dispatch({ type: 'COMPARE_QUEUE_CLEAR' })
  }, [])

  const clearAssessment = useCallback(() => {
    dispatch({ type: 'CLEAR_ASSESSMENT' })
  }, [])

  return {
    ...state,
    runAssessment,
    runAreaScan,
    runComparison,
    addToCompareQueue,
    removeFromCompareQueue,
    clearCompareQueue,
    clearAssessment,
  }
}
